import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useCreateTranscription } from '../hooks/useTranscription.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こし新規作成ページ。Sprint1はDrive URLタブのみ。タイトルとURLを入力し文字起こしを開始する。
 * @graph-connects useCreateTranscription [calls] 作成mutation hook
 */
export function TranscriptionCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateTranscription();
  const [title, setTitle] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');

  const isValid = title.trim().length > 0 && sourceUrl.includes('drive.google.com');

  const handleSubmit = () => {
    if (!isValid) return;
    createMutation.mutate(
      { title: title.trim(), sourceType: 'drive_video', sourceUrl },
      {
        onSuccess: () => {
          void navigate({ to: '/transcriptions' });
        },
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10">
      <button
        type="button"
        onClick={() => {
          void navigate({ to: '/transcriptions' });
        }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-primary)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('common.backToList')}
      </button>

      <div className="mb-6">
        <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
          New Transcription
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('common.createNew')}
        </h1>
      </div>

      <Card className="p-6">
        <div className="mb-6 border-b border-[var(--color-border)]">
          <button
            type="button"
            className="border-b-2 border-[var(--color-accent-primary)] px-4 pb-3 text-sm font-medium text-[var(--color-accent-primary)]"
          >
            {t('transcription.source.driveVideo')}
          </button>
          <button
            type="button"
            disabled
            className="px-4 pb-3 text-sm font-medium text-[var(--color-text-muted)] opacity-50"
          >
            {t('transcription.create.tabUpload')}
          </button>
          <button
            type="button"
            disabled
            className="px-4 pb-3 text-sm font-medium text-[var(--color-text-muted)] opacity-50"
          >
            {t('transcription.create.tabAiFassy')}
          </button>
          <button
            type="button"
            disabled
            className="px-4 pb-3 text-sm font-medium text-[var(--color-text-muted)] opacity-50"
          >
            {t('transcription.create.tabGoogleDocs')}
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label
              htmlFor="title"
              className="mb-1.5 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              {t('common.title')}
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              placeholder={t('transcription.create.titlePlaceholder')}
              maxLength={100}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)]/60 focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/10"
            />
          </div>
          <div>
            <label
              htmlFor="sourceUrl"
              className="mb-1.5 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              {t('transcription.create.driveUrlLabel')}
            </label>
            <input
              id="sourceUrl"
              type="url"
              value={sourceUrl}
              onChange={(e) => {
                setSourceUrl(e.target.value);
              }}
              placeholder="https://drive.google.com/file/d/..."
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)]/60 focus:border-[var(--color-accent-primary)] focus:ring-2 focus:ring-[var(--color-accent-primary)]/10"
            />
            <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">
              {t('transcription.create.driveHint')}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || createMutation.isPending}
          >
            {createMutation.isPending
              ? t('transcription.create.creating')
              : t('transcription.create.start')}
          </Button>
          {createMutation.isError ? (
            <p className="text-sm text-[var(--color-error)]">
              {t('transcription.create.error')}:{' '}
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : t('common.unknownError')}
            </p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
